"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeViewQueryState = void 0;
const ramda_1 = require("ramda");
const view_query_params_models_1 = require("./view-query-params.models");
const isValidNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isDefaultTableFilter = (filter) => filter.items.length === 0 && filter.logicOperator === 'and';
const viewQueryStateComparableFields = [
    view_query_params_models_1.ViewQueryStateField.ProjectId,
    view_query_params_models_1.ViewQueryStateField.CatalogId,
    view_query_params_models_1.ViewQueryStateField.Filter,
    view_query_params_models_1.ViewQueryStateField.Search,
    view_query_params_models_1.ViewQueryStateField.Sort,
    view_query_params_models_1.ViewQueryStateField.PageSize,
    view_query_params_models_1.ViewQueryStateField.DynamicColumns,
    view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter,
    view_query_params_models_1.ViewQueryStateField.SelectedTreeNodePath,
];
const hasField = (state, field) => (Object.prototype.hasOwnProperty.call(state, field));
const assignIfChanged = (normalized, state, defaults, field) => {
    if (hasField(state, field) && !(0, ramda_1.equals)(state[field], defaults[field])) {
        normalized[field] = state[field];
    }
};
const normalizeDynamicColumns = (dynamicColumns) => {
    var _a, _b, _c;
    if (!dynamicColumns)
        return undefined;
    const normalized = {};
    if ((_a = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ColumnsOrder]) === null || _a === void 0 ? void 0 : _a.length) {
        normalized[view_query_params_models_1.TableDynamicColumnsField.ColumnsOrder] = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ColumnsOrder];
    }
    if ((_b = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ExcludedColumns]) === null || _b === void 0 ? void 0 : _b.length) {
        normalized[view_query_params_models_1.TableDynamicColumnsField.ExcludedColumns] = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ExcludedColumns];
    }
    if ((_c = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.FilterOptions]) === null || _c === void 0 ? void 0 : _c.length) {
        normalized[view_query_params_models_1.TableDynamicColumnsField.FilterOptions] = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.FilterOptions];
    }
    if (dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.SelectAllMode]) {
        normalized[view_query_params_models_1.TableDynamicColumnsField.SelectAllMode] = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.SelectAllMode];
    }
    if (dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ReviewAttribute]) {
        normalized[view_query_params_models_1.TableDynamicColumnsField.ReviewAttribute] = dynamicColumns[view_query_params_models_1.TableDynamicColumnsField.ReviewAttribute];
    }
    return (0, ramda_1.isEmpty)(normalized) ? undefined : normalized;
};
const normalizeComparableState = (state) => {
    var _a;
    const normalized = {};
    if (state[view_query_params_models_1.ViewQueryStateField.ProjectId])
        normalized[view_query_params_models_1.ViewQueryStateField.ProjectId] = state[view_query_params_models_1.ViewQueryStateField.ProjectId];
    if (state[view_query_params_models_1.ViewQueryStateField.CatalogId])
        normalized[view_query_params_models_1.ViewQueryStateField.CatalogId] = state[view_query_params_models_1.ViewQueryStateField.CatalogId];
    if (state[view_query_params_models_1.ViewQueryStateField.Filter] && !isDefaultTableFilter(state[view_query_params_models_1.ViewQueryStateField.Filter])) {
        normalized[view_query_params_models_1.ViewQueryStateField.Filter] = state[view_query_params_models_1.ViewQueryStateField.Filter];
    }
    if (state[view_query_params_models_1.ViewQueryStateField.Search])
        normalized[view_query_params_models_1.ViewQueryStateField.Search] = state[view_query_params_models_1.ViewQueryStateField.Search];
    if (state[view_query_params_models_1.ViewQueryStateField.Sort] === null)
        normalized[view_query_params_models_1.ViewQueryStateField.Sort] = null;
    if (state[view_query_params_models_1.ViewQueryStateField.Sort] && !((_a = state[view_query_params_models_1.ViewQueryStateField.Sort]) === null || _a === void 0 ? void 0 : _a[view_query_params_models_1.TableSortField.Default])) {
        normalized[view_query_params_models_1.ViewQueryStateField.Sort] = state[view_query_params_models_1.ViewQueryStateField.Sort];
    }
    if (isValidNumber(state[view_query_params_models_1.ViewQueryStateField.PageSize]) && state[view_query_params_models_1.ViewQueryStateField.PageSize] > 0) {
        normalized[view_query_params_models_1.ViewQueryStateField.PageSize] = state[view_query_params_models_1.ViewQueryStateField.PageSize];
    }
    const dynamicColumns = normalizeDynamicColumns(state[view_query_params_models_1.ViewQueryStateField.DynamicColumns]);
    if (dynamicColumns)
        normalized[view_query_params_models_1.ViewQueryStateField.DynamicColumns] = dynamicColumns;
    if (state[view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter] === null) {
        normalized[view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter] = null;
    }
    if (state[view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter]) {
        normalized[view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter] = state[view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter];
    }
    if (state[view_query_params_models_1.ViewQueryStateField.SelectedTreeNodePath]) {
        normalized[view_query_params_models_1.ViewQueryStateField.SelectedTreeNodePath] = state[view_query_params_models_1.ViewQueryStateField.SelectedTreeNodePath];
    }
    return normalized;
};
const normalizeViewQueryState = (state, defaults = {}) => {
    const normalized = { [view_query_params_models_1.ViewQueryStateField.Version]: view_query_params_models_1.VIEW_QUERY_STATE_VERSION };
    const comparableState = normalizeComparableState(state);
    const comparableDefaults = normalizeComparableState(defaults);
    viewQueryStateComparableFields.forEach((field) => assignIfChanged(normalized, comparableState, comparableDefaults, field));
    return Object.keys(normalized).length === 1 ? null : normalized;
};
exports.normalizeViewQueryState = normalizeViewQueryState;
